/**
 * riskAnalyticsService — Unified data access layer for the Risk Engine.
 *
 * Every analytical dashboard MUST consume data through this service.
 * It encapsulates existing queries without altering their behavior and
 * adds transparent audit logging for governance traceability.
 */
import { supabase } from '@/integrations/supabase/client';

// ── Audit helper (fire-and-forget) ─────────────────────────────────
async function auditQuery(params: {
  dashboard: string;
  query_type: string;
  filters?: Record<string, unknown>;
  cluster_id?: string | null;
  contract_id?: string | null;
  started_at: number;
}) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const execution_time_ms = Date.now() - params.started_at;
    await supabase.from('risk_query_audit' as any).insert({
      user_id: user?.id ?? null,
      dashboard: params.dashboard,
      query_type: params.query_type,
      filters: params.filters ?? null,
      cluster_id: params.cluster_id ?? null,
      contract_id: params.contract_id ?? null,
      execution_time_ms,
    });
  } catch {
    // Non-blocking — audit failure must never break dashboards
  }
}

// ── 1. Contract Risk Ranking ────────────────────────────────────────
export async function getContractRiskRanking(clusterId?: string) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_contract_ranking')
    .select('*')
    .order('score_criticidade', { ascending: false });

  if (clusterId) query = query.eq('cluster_id', clusterId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'inteligencia', query_type: 'contract_ranking', cluster_id: clusterId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 2. Risk Trends ──────────────────────────────────────────────────
export async function getRiskTrends(contractId?: string) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_trends')
    .select('*')
    .order('data_referencia', { ascending: true });

  if (contractId) query = query.eq('contract_id', contractId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'inteligencia', query_type: 'risk_trends', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 3. Risk Anomalies ───────────────────────────────────────────────
export async function getRiskAnomalies(contractId?: string, limit = 5) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_anomalies')
    .select('*')
    .order('detectado_em', { ascending: false })
    .limit(limit);

  if (contractId) query = query.eq('contract_id', contractId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'inteligencia', query_type: 'risk_anomalies', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 4. UO Heatmap ───────────────────────────────────────────────────
export async function getRiskUOHeatmap(contractId?: string) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_uo_heatmap')
    .select('*')
    .order('score_risco_uo', { ascending: false, nullsFirst: false });

  if (contractId) query = query.eq('contract_id', contractId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'inteligencia', query_type: 'uo_heatmap', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 5. Cluster Risk Summary (KPIs) ─────────────────────────────────
export async function getClusterRiskSummary(clusterId?: string) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_kpis_cluster' as any)
    .select('*')
    .order('score_risco', { ascending: false })
    .limit(200);

  if (clusterId) query = query.eq('cluster_id', clusterId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'analytics', query_type: 'cluster_summary', cluster_id: clusterId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 6. Monte Carlo Results ──────────────────────────────────────────
export async function getMonteCarloResults(contractId: string) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('risk_monte_carlo_runs')
    .select('*')
    .eq('contract_id', contractId)
    .eq('status', 'completed')
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  auditQuery({ dashboard: 'inteligencia', query_type: 'monte_carlo_latest', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data;
}

// ── 7. Contract KPIs ────────────────────────────────────────────────
export async function getContractKPIs(contractId: string) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_risk_kpis_contrato' as any)
    .select('*')
    .eq('contract_id', contractId)
    .order('periodo', { ascending: false })
    .limit(12);

  auditQuery({ dashboard: 'inteligencia', query_type: 'contract_kpis', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 8. Monte Carlo Latest (view) ────────────────────────────────────
export async function getMonteCarloLatest(contractId: string) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_risk_monte_carlo_latest' as any)
    .select('*')
    .eq('contract_id', contractId)
    .limit(1);

  auditQuery({ dashboard: 'inteligencia', query_type: 'monte_carlo_view', contract_id: contractId, started_at: t });
  if (error) throw error;
  return data && data.length > 0 ? data[0] : null;
}

// ── 9. Risk Alerts ──────────────────────────────────────────────────
export async function getRiskAlerts() {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_alertas_ativos')
    .select('*')
    .order('horas_desde_disparo', { ascending: false });

  auditQuery({ dashboard: 'alertas', query_type: 'alertas_ativos', started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 10. Trend History (cluster) ─────────────────────────────────────
export async function getRiskTrendHistory(clusterId?: string) {
  const t = Date.now();
  let query = supabase
    .from('vw_risk_trend_history' as any)
    .select('*')
    .order('periodo', { ascending: false })
    .limit(500);

  if (clusterId) query = query.eq('cluster_id', clusterId);

  const { data, error } = await query;
  auditQuery({ dashboard: 'analytics', query_type: 'trend_history', cluster_id: clusterId, started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 11. Operational Analytics Views (used by RiskAnalyticsDashboard) ─
export async function getConcentracaoTempoReal() {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_concentracao_risco_tempo_real')
    .select('*')
    .order('percentual_critico', { ascending: false });

  auditQuery({ dashboard: 'analytics', query_type: 'concentracao_tempo_real', started_at: t });
  if (error) throw error;
  return data || [];
}

export async function getRankingObrasCriticas(limit = 20) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_ranking_obras_criticas')
    .select('*')
    .order('score_criticidade', { ascending: false })
    .limit(limit);

  auditQuery({ dashboard: 'analytics', query_type: 'ranking_obras', started_at: t });
  if (error) throw error;
  return data || [];
}

export async function getDistribuicaoClasses() {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_distribuicao_classes_risco')
    .select('*')
    .order('total_ocorrencias', { ascending: false });

  auditQuery({ dashboard: 'analytics', query_type: 'distribuicao_classes', started_at: t });
  if (error) throw error;
  return data || [];
}

export async function getTendenciaSemanal(limit = 100) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_tendencia_risco_semanal')
    .select('*')
    .order('semana', { ascending: false })
    .limit(limit);

  auditQuery({ dashboard: 'analytics', query_type: 'tendencia_semanal', started_at: t });
  if (error) throw error;
  return data || [];
}

export async function getPadroesReincidencia(limit = 50) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_padroes_reincidencia')
    .select('*')
    .order('ocorrencias', { ascending: false })
    .limit(limit);

  auditQuery({ dashboard: 'analytics', query_type: 'padroes_reincidencia', started_at: t });
  if (error) throw error;
  return data || [];
}

export async function getIndicadoresMensais(limit = 24) {
  const t = Date.now();
  const { data, error } = await supabase
    .from('vw_indicadores_mensais_comparativos')
    .select('*')
    .order('mes', { ascending: false })
    .limit(limit);

  auditQuery({ dashboard: 'analytics', query_type: 'indicadores_mensais', started_at: t });
  if (error) throw error;
  return data || [];
}

// ── 12. Pipeline Monitor ────────────────────────────────────────────
export async function getPipelineStats() {
  const t = Date.now();
  const today = new Date().toISOString().slice(0, 10);

  const [pendingRes, processingRes, processedRes, logsRes, runsRes] = await Promise.all([
    supabase.from('risk_pending_queue' as any).select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase.from('risk_pending_queue' as any).select('id', { count: 'exact', head: true }).eq('status', 'processing'),
    supabase.from('risk_pending_queue' as any).select('id', { count: 'exact', head: true }).eq('status', 'processed').gte('processed_at', today),
    supabase.from('risk_simulation_logs' as any).select('id, tipo, acao, created_at, dados_snapshot').order('created_at', { ascending: false }).limit(10),
    supabase.from('risk_monte_carlo_runs').select('id, contract_id, status, tipo, n_simulations, created_at, completed_at').order('created_at', { ascending: false }).limit(20),
  ]);

  auditQuery({ dashboard: 'monitor', query_type: 'pipeline_stats', started_at: t });

  return {
    queue: {
      pending: pendingRes.count ?? 0,
      processing: processingRes.count ?? 0,
      processed_today: processedRes.count ?? 0,
    },
    logs: (logsRes.data || []) as any[],
    runs: (runsRes.data || []) as any[],
  };
}

// ── 13. Engine Health Metrics ────────────────────────────────────────
export async function getEngineHealthMetrics() {
  const t = Date.now();

  // Last completed run
  const { data: lastRun } = await supabase
    .from('risk_monte_carlo_runs')
    .select('completed_at, created_at, status')
    .eq('status', 'completed')
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // Failed runs in last 24h
  const yesterday = new Date(Date.now() - 86400000).toISOString();
  const { count: failCount } = await supabase
    .from('risk_monte_carlo_runs')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'failed')
    .gte('created_at', yesterday);

  // Average duration of last 10 completed runs
  const { data: recentRuns } = await supabase
    .from('risk_monte_carlo_runs')
    .select('created_at, completed_at')
    .eq('status', 'completed')
    .order('completed_at', { ascending: false })
    .limit(10);

  let avgDurationMs = 0;
  if (recentRuns && recentRuns.length > 0) {
    const durations = recentRuns
      .filter(r => r.completed_at && r.created_at)
      .map(r => new Date(r.completed_at!).getTime() - new Date(r.created_at).getTime());
    avgDurationMs = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
  }

  // NLP processing errors (last 24h from simulation logs)
  const { count: nlpErrors } = await supabase
    .from('risk_simulation_logs' as any)
    .select('id', { count: 'exact', head: true })
    .eq('tipo', 'error')
    .gte('created_at', yesterday);

  auditQuery({ dashboard: 'monitor', query_type: 'engine_health', started_at: t });

  return {
    lastRunAt: lastRun?.completed_at ?? null,
    failuresLast24h: failCount ?? 0,
    avgDurationMs: Math.round(avgDurationMs),
    nlpErrorsLast24h: nlpErrors ?? 0,
  };
}
